// Layout of the email builder (ported from TrainYourPulse's webapp marketing-tool/emails/builder).
// Only the body is built here - the server wraps it into our header/footer (mailer.util), so there are
// no logo / social blocks and no page settings.

export type Align = "left" | "center" | "right";

export type EmailBlockType =
  | "heading"
  | "paragraph"
  | "image"
  | "button"
  | "divider"
  | "spacer"
  | "columns";

interface BaseBlock {
  id: string;
  type: EmailBlockType;
  backgroundColor?: string;
}

export interface HeadingBlock extends BaseBlock {
  type: "heading";
  text: string; // inline HTML (bold / italic / links)
  align: Align;
  fontSize: number;
  color: string;
  padding: number;
}

export interface ParagraphBlock extends BaseBlock {
  type: "paragraph";
  text: string; // inline HTML (bold / italic / links)
  align: Align;
  fontSize: number;
  color: string;
  padding: number;
}

export interface ImageBlock extends BaseBlock {
  type: "image";
  url: string;
  alt: string;
  link: string;
  width: number;
  align: Align;
  padding: number;
}

export interface ButtonBlock extends BaseBlock {
  type: "button";
  text: string;
  link: string;
  bgColor: string;
  textColor: string;
  align: Align;
  borderRadius: number;
  padding: number;
}

export interface DividerBlock extends BaseBlock {
  type: "divider";
  color: string;
  thickness: number;
  margin: number;
  widthPercent: number;
}

export interface SpacerBlock extends BaseBlock {
  type: "spacer";
  height: number;
}

export interface ColumnDef {
  widthPercent: number;
  blocks: EmailBlock[];
}

export interface ColumnsBlock extends BaseBlock {
  type: "columns";
  columns: ColumnDef[];
}

export type EmailBlock =
  | HeadingBlock
  | ParagraphBlock
  | ImageBlock
  | ButtonBlock
  | DividerBlock
  | SpacerBlock
  | ColumnsBlock;

export interface EmailLayout {
  blocks: EmailBlock[];
}

// What the builder edits: a block layout, or HTML written / imported by hand (also every template
// saved before the builder existed)
export type EmailBuilderValue =
  | { mode: "blocks"; layout: EmailLayout }
  | { mode: "html"; html: string };

// Same width as the content wrapper of mailer.util#createEmailContentFromEmailTemplate
export const EMAIL_CONTENT_WIDTH = 552;
export const MOBILE_PREVIEW_WIDTH = 375;
// A 375px phone minus the 24px side paddings of the wrapper
export const MOBILE_CONTENT_WIDTH = MOBILE_PREVIEW_WIDTH - 48;
export const EMAIL_FONT = "'Open Sans', Arial, sans-serif";

const BRAND_COLOR = "#ff8c42";
const TEXT_COLOR = "#101016";

const FIRST_NAME_CONSTANT = "{client_firstname}";
const LINK_CONSTANT = "{link_address}";

// {constants} the server replaces when sending - anything it doesn't know for the email becomes empty
export const findConstants = (html: string) =>
  Array.from(new Set(html.match(/\{[a-z_]+\}/g) || []));

export const newBlockId = () => "blk_" + Math.random().toString(36).slice(2, 9);

export const createDefaultBlock = (type: Exclude<EmailBlockType, "columns">): EmailBlock => {
  const id = newBlockId();
  switch (type) {
    case "heading":
      return {
        id,
        type,
        text: "Heading",
        align: "left",
        fontSize: 24,
        color: TEXT_COLOR,
        padding: 10,
      };
    case "paragraph":
      return {
        id,
        type,
        text: "Paragraph text goes here...",
        align: "left",
        fontSize: 16,
        color: TEXT_COLOR,
        padding: 10,
      };
    case "image":
      return { id, type, url: "", alt: "", link: "", width: 552, align: "center", padding: 0 };
    case "button":
      return {
        id,
        type,
        text: "Click here",
        link: "",
        bgColor: BRAND_COLOR,
        textColor: "#ffffff",
        align: "center",
        borderRadius: 5,
        padding: 12,
      };
    case "divider":
      return { id, type, color: "#e0e0e0", thickness: 1, margin: 16, widthPercent: 100 };
    case "spacer":
      return { id, type, height: 20 };
  }
};

export const createColumns = (count: 1 | 2 | 3): ColumnsBlock => {
  const percent = Math.floor(100 / count);
  const extra = 100 - percent * count;
  return {
    id: newBlockId(),
    type: "columns",
    columns: Array.from({ length: count }).map((_, i) => ({
      widthPercent: percent + (i === 0 ? extra : 0),
      blocks: [],
    })),
  };
};

// A new block; a button links to {link_address} when the email has one
export const createBlockFor = (
  type: Exclude<EmailBlockType, "columns">,
  constants: string[]
): EmailBlock => {
  const block = createDefaultBlock(type);
  if (block.type === "button" && constants.includes(LINK_CONSTANT)) block.link = LINK_CONSTANT;
  return block;
};

export const createDefaultLayout = (constants: string[] = []): EmailLayout => {
  const heading = createDefaultBlock("heading") as HeadingBlock;
  // Not every email knows the recipient's name (e.g. a referral invitation)
  heading.text = constants.includes(FIRST_NAME_CONSTANT) ? `Hi ${FIRST_NAME_CONSTANT},` : "Hi,";
  const paragraph = createDefaultBlock("paragraph");
  return { blocks: [heading, paragraph] };
};

// A JSON layout saved by the builder, or null when it doesn't look like one
export const parseLayout = (json: string | null | undefined): EmailLayout | null => {
  if (!json) return null;
  try {
    const layout = JSON.parse(json);
    return layout && Array.isArray(layout.blocks) ? (layout as EmailLayout) : null;
  } catch {
    return null;
  }
};
